import React from "react";
import { History, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { fetchExportHistory, type ExportHistoryEntry } from "@/lib/documentGenerator/exportHistory";
import { PHASE_TEMPLATE } from "@/lib/basProcess/phases";

const phaseLabel = (key?: string | null) =>
  key ? PHASE_TEMPLATE.find((p) => p.key === key)?.name ?? key : "Skede ej angivet";

export interface ExportHistoryTarget {
  documentType: string;
  documentId?: string | null;
  projectId?: string | null;
  /** Rubrik i dialogen */
  title?: string;
}

const formatDate = (value: string) =>
  new Date(value).toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" });

const SnapshotPreview: React.FC<{ entry: ExportHistoryEntry }> = ({ entry }) => {
  const data = (entry.snapshot as { data?: unknown } | null)?.data;
  if (!data) return null;
  return (
    <details className="mt-2">
      <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
        Visa uppgifter som användes
      </summary>
      <pre className="mt-2 max-h-56 overflow-auto rounded-lg bg-muted/60 p-3 text-[11px] leading-relaxed">
        {JSON.stringify(data, null, 2)}
      </pre>
    </details>
  );
};

/** Visar versionshistorik för Word-exporter av ett dokument. */
export const ExportHistoryDialog: React.FC<{ target: ExportHistoryTarget }> = ({ target }) => {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [entries, setEntries] = React.useState<ExportHistoryEntry[]>([]);

  React.useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    fetchExportHistory(target)
      .then((rows) => active && setEntries(rows))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target.documentType, target.documentId, target.projectId]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <History className="h-4 w-4" />
          Versionshistorik
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{target.title || "Versionshistorik – Word-export"}</DialogTitle>
          <DialogDescription>
            Varje Word-export sparas med datum, versionsnummer och de uppgifter som låg till grund för
            dokumentet.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-3">
          {loading && <p className="py-6 text-sm text-muted-foreground">Hämtar historik…</p>}

          {!loading && entries.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">
              Inga exporter är loggade ännu. Nästa Word-export sparas som version 1.
            </p>
          )}

          <ul className="space-y-3">
            {entries.map((entry) => (
              <li key={entry.id} className="rounded-xl border border-border/60 bg-card p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                      Version {entry.version}
                    </span>
                    <span className="text-sm font-medium">
                      {entry.document_name || entry.filename}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md border border-border/70 px-2 py-0.5 text-[11px] text-muted-foreground">
                      {phaseLabel(entry.phase_key)}
                    </span>
                    <span className="text-xs text-muted-foreground">{formatDate(entry.created_at)}</span>
                  </div>
                </div>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <FileText className="h-3.5 w-3.5" />
                  {entry.filename}
                </p>
                {entry.verification_code && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Verifieringskod:{" "}
                    <span className="font-mono text-foreground">{entry.verification_code}</span>
                    {entry.revoked_at ? " · återkallad" : ""}
                  </p>
                )}
                <SnapshotPreview entry={entry} />

              </li>
            ))}
          </ul>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default ExportHistoryDialog;
